import React, { useCallback, useEffect, useLayoutEffect, useState } from 'react';
import clsx from 'clsx';
import { usePreference, useUser } from 'hooks';
import { useLocation } from '@reach/router';
import useError from 'hooks/useError';
import { useSelector } from 'react-redux';
import { RootState } from 'redux/reducers/type';
import { makeStyles, Theme, Typography } from '@material-ui/core';
import {
  Grid,
  Container,
  Box,
  Card,
  CardContent,
  CardActions,
  Button,
  InputBase,
} from '@material-ui/core';
import { Footer } from 'components/Footer';
import { BannerModal } from 'components/Modal';
import { LoginBackground, LoginLogo, LoginBackgroundMobile, HeaderLogo } from 'asset/images';

const useStyles = makeStyles((theme: Theme) => ({
  loginContentsContainer: {
    width: '100%',
    height: '60vh',
    minHeight: '460px',
    backgroundColor: `rgba(1, 8, 22, 1.0)`,
    [theme.breakpoints.down('md')]: {
      height: 'auto',
      minWidth: '320px',
      minHeight: 'auto',
    },
  },
  lognLogoContainer: {
    position: 'absolute',
    left: 0,
    right: 0,
    marginLeft: 'auto',
    marginRight: 'auto',
    zIndex: 1,
    height: '230px',
    minWidth: '1200px',
    maxWidth: '1280px',
    background: `center top / 90% no-repeat url(${LoginBackground})`,
    backgroundSize: 'cover',
    [theme.breakpoints.down('md')]: {
      position: 'relative',
      height: '36vw',
      minWidth: '320px',
      maxWidth: 'auto',
      minHeight: '116px',
      background: `center top / 90% no-repeat url(${LoginBackgroundMobile})`,
      backgroundSize: 'cover',
    },
  },
  loginContents: {
    position: 'relative',
    zIndex: 20,
    height: '100%',
    maxWidth: '400px',
    marginLeft: 'auto',
    marginRight: 'auto',
    paddingBottom: '10vh',
    [theme.breakpoints.down('md')]: {
      maxWidth: '320px',
      paddingLeft: '24px',
      paddingRight: '24px',
      paddingBottom: '85px',
    },
  },
  loginCard: {
    width: '100%',
    backgroundColor: 'rgba(1, 8, 22, 0.5)',
  },
  loginLogo: {
    margin: '1em 1em 0 1em',
    maxWidth: '100%',
    [theme.breakpoints.down('md')]: {
      margin: 'auto',
      marginBottom: '3px',
    },
  },
  loginInputItems: {
    paddingTop: '0',
    paddingBottom: '12px',
  },
  loginLabel: {
    color: 'rgba(255, 255, 255, 1.0)',
    fontSize: '14px',
    fontWeight: 'bold',
  },
  loginInput: {
    height: '32px',
    backgroundColor: '#FFFFFF',
    border: 'solid 1px #abacad',
    fontSize: '14px',
    '& > input': {
      paddingLeft: '15px',
      paddingRight: '15px',
    },
  },
  loginSubmit: {
    height: '40px',
    color: '#FFF',
    backgroundColor: '#d94f98',
    fontSize: '16px',
    fontWeight: 'bold',
    [theme.breakpoints.down('md')]: {
      marginLeft: '8px',
      marginRight: '8px',
    },
  },
  messageContainer: {
    color: '#FFF',
    textAlign: 'center',
    fontSize: '12px',
  },
}));

function Login() {
  const classes = useStyles();

  const { onLogin: onLoginUser, onAuthenticate } = useUser();
  const { onRequestPreferenceByBanner } = usePreference();

  const { error } = useSelector((state: RootState) => state.error);
  const { errorMessage, unValidated } = useSelector((state: RootState) => state.user);

  const { onClearError } = useError();

  const location = useLocation();

  const [userId, setUserId] = useState<string>('');
  const [password, setPassword] = useState<string>('');

  useEffect(() => {
    const { pathname, search } = location;
    const redirectUrl: string = pathname + search;
    onAuthenticate(redirectUrl);
    onRequestPreferenceByBanner();
  }, [onAuthenticate, onRequestPreferenceByBanner]);

  const onLogin = useCallback(
    (event: React.FormEvent) => {
      event.preventDefault();
      onLoginUser(userId, password);
      if (error) {
        onClearError();
      }
    },
    [onLoginUser, userId, password, error, onClearError],
  );

  const onChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const { value, name } = event.target;

    switch (name) {
      case 'userId': {
        setUserId(value);
        break;
      }
      case 'password': {
        setPassword(value);
        break;
      }
    }
  };

  const handleNotification = () => {
    if (!('Notification' in window)) {
      return;
    }

    const options = {
      body: 'notification body test',
      icon: HeaderLogo,
      onclick: (notification: Notification, ev: Event) => {
        console.log('Notification onclick test.');
      },
      onclose: (notification: Notification, ev: Event) => {
        console.log('Notification onclose test.');
      },
      onshow: () => {
        console.log('Notification onshow test.');
      },
      // dir: 'rtl' as NotificationDirection,
      // badge: HeaderLogo,
      // image: HeaderLogo,
      // tag: 'tag Test',
      // data: 'data test',
    };

    if (Notification.permission !== 'granted') {
      Notification.requestPermission().then(permission => {
        if (permission === 'granted') {
          /* 권한을 요청받고 nofi를 생성해주는 부분 */
          new Notification('TEST NOTIFICATION', options);
        } else {
          return;
        }
      });
    } else {
      /* 권한이 있을때 바로 noti 생성해주는 부분 */
      new Notification('TEST NOTIFICATION', options);
    }
  };

  return (
    <>
      <BannerModal />
      <Container disableGutters maxWidth={false} className={classes.loginContentsContainer}>
        <Box className={classes.lognLogoContainer} />
        <Grid
          container
          justifyContent="center"
          alignItems="flex-end"
          className={classes.loginContents}
        >
          <Grid item xs>
            <Card className={classes.loginCard}>
              <form onSubmit={onLogin}>
                <CardContent>
                  <img src={LoginLogo} className={classes.loginLogo} />
                </CardContent>
                <CardContent>
                  <Grid container className={classes.loginInputItems}>
                    <Grid item xs={3} container justifyContent="flex-start" alignContent="center">
                      <Typography className={classes.loginLabel}>아이디</Typography>
                    </Grid>
                    <Grid item xs>
                      <InputBase
                        className={classes.loginInput}
                        type="text"
                        placeholder="아이디를 입력하세요"
                        name="userId"
                        value={userId}
                        onChange={onChange}
                        fullWidth
                        autoFocus
                      />
                    </Grid>
                  </Grid>
                  <Grid container justifyContent="center" className={classes.loginInputItems}>
                    <Grid item xs={3} container justifyContent="flex-start" alignContent="center">
                      <Typography className={classes.loginLabel}>비밀번호</Typography>
                    </Grid>
                    <Grid item xs>
                      <InputBase
                        className={classes.loginInput}
                        type="password"
                        placeholder="비밀번호를 입력하세요"
                        name="password"
                        value={password}
                        onChange={onChange}
                        fullWidth
                      />
                    </Grid>
                  </Grid>
                </CardContent>
                <CardActions>
                  <Button
                    fullWidth
                    variant="contained"
                    type="submit"
                    className={classes.loginSubmit}
                  >
                    로그인
                  </Button>
                </CardActions>
                <CardContent>
                  {unValidated && (
                    <Typography className={clsx(classes.messageContainer)}>
                      {errorMessage}
                    </Typography>
                  )}
                </CardContent>
              </form>

              {/* <button onClick={handleNotification}>Notification</button> */}
            </Card>
          </Grid>
        </Grid>
      </Container>
      <Footer />
    </>
  );
}

export default Login;
